/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */
import { headerNavBarDropMenuItemSX } from '@/components/content/Header/styles/navBar/dropMenuItem';
import { HeaderNavBarMenuList } from '@/components/content/Header/parts/NavBarMenuList';
import { headerNavBarItemSX } from '@/components/content/Header/styles/navBar/item';
import { headerNavBarDropMenuSX } from '@/components/content/Header/styles/navBar/dropMenu';
import { PageLink } from '@/data/Navigation';
import { Switch } from '@/utils/switch';
import { Box, Button, ClickAwayListener, Stack, MenuItem, Grid, Skeleton } from '@mui/material';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import React, { useCallback, useEffect } from 'react';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { styled } from '@mui/material/styles';
import { LinkWrap } from '@/components/blocks/Linkable';
import { DpContainer } from '@/components/blocks/DpContainer';
import { combineSX } from '@/utils/combineSX';
import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { useLockBodyScroll } from '@/utils/lockBodyScroll';

type Props = {
	tree: PageLink[];
	label: string;
	url: string;
};

const NoMaxWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} classes={{ popper: className }} />
))({
	[`& .${tooltipClasses.popperInteractive}`]: {
		width: '100% !important',
	},
});

export const HeaderNavBarDropMenu = React.forwardRef(
	({ tree, label, url, ...props }: Props, ref: React.Ref<HTMLAnchorElement> | null) => {
		const router = useNextRouter();
		const [open, setOpen] = React.useState(false);
		const [newPath, setNewPath] = React.useState(true);

		useLockBodyScroll(open);

		const handleToolTip = useCallback(
			(action?: string) => () =>
				setOpen((open) =>
					Switch(action)
						.case('open', () => true)
						.case('close', () => false)
						.defaultTo(() => !open)
				),
			[]
		);

		useEffect(() => {
			if (!router.asPath) return;
			handleToolTip('close')();
			const timeout = setTimeout(() => {
				setNewPath(false);
			}, 1000);
			return () => clearTimeout(timeout);
		}, [handleToolTip, router.asPath]);

		const handleMouseEnter = () => {
			if (!newPath) {
				handleToolTip('open')();
				setNewPath(true);
			}
		};

		const handleMouseLeave = () => {
			handleToolTip('close')();
		};

		return (
			<ClickAwayListener onClickAway={handleToolTip('close')}>
				<Stack onMouseLeave={handleMouseLeave} height="100%">
					<NoMaxWidthTooltip
						sx={{
							'&::before': {
								content: '""',
								position: 'fixed',
								top: 0,
								left: 0,
								width: '100vw',
								height: '100vh',
								backgroundColor: 'rgba(0, 0, 0, 0.5)',
								opacity: open ? 1 : 0,
								zIndex: -1,
							},
						}}
						placement="bottom-start"
						open={open}
						onClose={handleToolTip('close')}
						disableFocusListener
						disableHoverListener
						disableTouchListener
						PopperProps={{
							disablePortal: true,
							modifiers: [
								{
									name: 'offset',
									options: {
										offset: [0, -1],
									},
								},
							],
							sx: {
								zIndex: 1300,
							},
						}}
						componentsProps={{
							tooltip: {
								sx: headerNavBarDropMenuSX,
								onMouseEnter: handleMouseEnter,
								onMouseLeave: handleMouseLeave,
							},
						}}
						title={
							tree && tree.length > 0 ? (
								<Stack>
									<Stack direction="row" justifyContent="center">
										<DpContainer
											sx={{
												display: 'Grid',
												gridTemplateColumns: '1fr 2fr',
												// columnGap: '4rem',
												paddingTop: '15px',
											}}
										>
											<Grid
												display="flex"
												flexDirection="column"
												alignItems="flex-start"
												sx={{ mt: '40px' }}
											>
												<ContentRecommendation
													id={`DPV2_Header_${label}`}
													properties={{
														emsName: `DPV2_Header_${label}`,
													}}
													loadingElement={
														<Stack spacing={1}>
															<Skeleton variant="rounded" width={307} height={200} />
															<Skeleton variant="rounded" width={180} height={'15px'} />
															<Skeleton variant="rounded" width={100} height={'15px'} />
														</Stack>
													}
												/>
											</Grid>

											<Grid xs display="flex" flexDirection="column">
												<HeaderNavBarMenuList
													handleToolTip={handleToolTip}
													tree={tree}
													level={1}
													url={url}
												/>
												<LinkWrap href={url}>
													<MenuItem
														component="a"
														onClick={handleToolTip('close')}
														sx={combineSX([
															headerNavBarDropMenuItemSX({ isParent: false }),
															{
																display: 'inline-flex',
																alignItems: 'center',
																alignSelf: 'flex-start',
																mt: '20px',
																py: '0px',
																px: 0.75,
																fontWeight: 600,
															},
														])}
													>
														{'Ver todo'} {label}
													</MenuItem>
												</LinkWrap>
											</Grid>
										</DpContainer>
									</Stack>
								</Stack>
							) : null
						}
					>
						<Box
							sx={{
								zIndex: 1301,
								height: '100%',
								display: 'flex',
								alignItems: 'stretch',
							}}
							onMouseEnter={handleToolTip('open')}
							onTouchStart={handleToolTip('close')}
						>
							<Button
								component="a"
								ref={ref}
								{...props}
								sx={{
									...headerNavBarItemSX,
									...(open
										? {
												'&.MuiButton-text': {
													...((headerNavBarItemSX as any) || {})?.['&.MuiButton-text'],
													'&:after': {
														...((headerNavBarItemSX as any) || {})?.['&.MuiButton-text']?.[
															'&:after'
														],
														display: 'block !important',
													},
												},
										  }
										: {}),
								}}
							>
								{label}
							</Button>
						</Box>
					</NoMaxWidthTooltip>
				</Stack>
			</ClickAwayListener>
		);
	}
);
